import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DeckSlide } from "@/components/decks/DeckSlide";
import { DECK } from "@/constants/testIds";
import api from "@/lib/api";

export default function PublicDeck() {
  const { token } = useParams();
  const [pres, setPres] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/presentations/${token}`);
      setPres(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : d?.message || "This presentation is not available.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 text-center text-sm text-muted-foreground">
        {err}
      </div>
    );
  }

  const base = api.defaults.baseURL;
  return (
    <div data-testid={DECK.publicRoot} className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60 px-6 py-4">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" />
            <span className="text-sm font-semibold">sema</span>
          </a>
          <a href={`${base}/public/presentations/${token}/download?format=pptx`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="rounded-full text-xs"><Download className="mr-1.5 h-3.5 w-3.5" /> PPTX</Button>
          </a>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl px-6 py-8">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{pres.title}</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{pres.subtitle}</p>

        {pres.summary?.length > 0 && (
          <section data-testid={DECK.summary} className="mt-8 rounded-xl border border-accent/30 bg-accent/5 p-5">
            <h3 className="text-xs uppercase tracking-[0.16em] text-accent">Executive summary</h3>
            <ul className="mt-3 space-y-2">
              {pres.summary.map((b, i) => (
                <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-foreground/90">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />{b}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-6 space-y-4">
          {(pres.slides || []).map((s, i) => <DeckSlide key={i} slide={s} />)}
        </div>

        <footer className="mt-12 border-t border-border/60 pt-6 text-center text-xs text-muted-foreground">
          Built with <a href="/" className="text-accent">Sema</a> — the semantic layer for your data.
        </footer>
      </main>
    </div>
  );
}
